import { FC, useMemo } from 'react';
import Page, { PageProps } from '../../components/page';
import { Box, Stack, Theme } from '@mui/material';
import Icon from '../../components/icon';
import Text from '../../components/text';
import LoginForm from './login-form';
import PageCard from '@/components/page-card';
import { getSite } from '@/lib/services/auth.service';
import { createUseStyles } from 'react-jss';
import Lockup from '@/assets/f5-wais-light.svg';
import useApp from '@/hooks/use-app.hook';
import { useSearch } from '@tanstack/react-router';
import { getHostname } from '@/lib/url-helpers';
import useThemeMediaQuery from '@/hooks/use-theme-media-query.hook';

const useStyles = createUseStyles((theme: Theme) => ({
  siteContainer: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: '12px 16px',
    marginBottom: 16,
  },
  loginCard: {
    padding: 0,
  },
}));

const LoginPage: FC<PageProps> = () => {
  const styles = useStyles();
  const { medium } = useThemeMediaQuery();
  const { branding } = useApp();
  const { redirect_to: redirectUrl = '' } = useSearch({ from: '/login' });
  const hostname = getHostname(redirectUrl);

  const redirectDisplayUrl = hostname ? `, like ${hostname},` : '';

  const [siteLabel, showLogo] = useMemo(() => {
    const site = getSite();
    switch (site) {
      case 'admin':
        return ['Admin Console', true];
      case 'partner':
        return ['Partner Console', true];
      case 'portal':
        return ['Portal', false];
    }

    return ['', false];
  }, []);

  const hasSiteLabel = !!siteLabel;
  const { name: orgName } = branding;

  return (
    <Page>
      <PageCard className={styles.loginCard}>
        {hasSiteLabel && (
          <Stack
            width="100%"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            className={styles.siteContainer}
          >
            {showLogo ? (
              <img src={Lockup} alt="F5 Workforce AI Security" width={200} />
            ) : (
              <Text>{orgName}</Text>
            )}
            <Text>{siteLabel}</Text>
          </Stack>
        )}

        <Stack alignItems="center" justifyContent="center" gap={2} p={medium ? 1 : 2} width="100%">
          <Box m={0.5}>
            <Icon name="lock" background="first" />
          </Box>
          <Text center size="x-large">
            {hasSiteLabel ? 'Sign in' : 'Authentication Required'}
          </Text>
          {!hasSiteLabel && (
            <Box m={2} textAlign="center">
              <Text size="medium">
                {orgName} provides managed access to GenAI tools{redirectDisplayUrl} with enhanced
                privacy and safety. Please enter your work email to continue.
              </Text>
            </Box>
          )}
          <Box mt={hasSiteLabel ? 6 : 1} width={medium ? '90%' : '84%'} minHeight={260}>
            <LoginForm />
          </Box>
        </Stack>
      </PageCard>
    </Page>
  );
};

export default LoginPage;
